var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import cloneDeep from 'clone-deep';
import _ from 'lodash';
import moment from 'moment';
import { stringify } from 'query-string';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { apiGetCategorySelect } from '@/actions/CategoryActions';
import { apiGetOneProfessionalSeminar, apiCreateProfessionalSeminar, apiUpdateProfessionalSeminar, apiCreateProfessionalSeminarClone, } from '@/actions/ProfessionalSeminarActions';
import { SEMINAR_INFORMATION_STATUS_DRAFT, SEMINAR_INFORMATION_DATA_TYPE_VIEW, SEMINAR_INFORMATION_DATA_TYPE_PREVIEW, } from '@/define/consts/seminar/seminar_information';
import { useResourceUpload } from '@/hooks/file/useResource';
import { useNavigate } from '@/hooks/lib/react-router/useNavigate';
import SeminarOneForm from '../../../container/Professional/Seminar/One/Parts/SeminarOneForm';
import FinalFormSpy from '../../../container/Ui/Common/FinalForm/FinalFormSpy';
import { notify, notifyError, wrapDispatch, wrapNotifyError, } from '../../action';
import { toDotArray } from '../../common/ArrayHelper';
import { formValidateScroll } from '../../form';
import { useDidMount, usePromiseDoingCallback } from '../useHook';
/**
 * セミナーの編集を行うuse
 * @param options
 * @returns {*}
 */
export const useProfessionalSeminarEdit = (options) => {
    const { dispatch, id, profile, seminar, unmountRef } = options;
    const history = useNavigate();
    const formRef = useRef({});
    const [formSpyState, changeFormSpyState] = useState({});
    const categories = useSelector((state) => state.category.seminarCategories);
    // 新規作成もしくは、自身が作成したセミナーか
    const isSelf = !id || seminar.isSelf;
    const { pristine } = formSpyState;
    const { email, information: profileInformation } = profile;
    // 日付選択datepicker用のprops
    const datepickerProps = useMemo(() => {
        const now = moment();
        return {
            minDate: now.clone().startOf('date').toDate(),
            now: now.toDate(),
        };
    }, []);
    /**
     * デフォルト値で使い回す値
     * @type {*}
     */
    const defaultValues = useMemo(() => ({
        dates: [{}],
        documentFile: {},
        handlePersonalInformation: '申し込み時にご提供いただいた情報は、イベント受付管理や各種ご案内（サービス・イベント等）に利用させていただきます。',
        liaisons: [
            {
                email,
                firstName: profileInformation.firstName,
                firstNameKana: profileInformation.firstNameKana,
                lastName: profileInformation.lastName,
                lastNameKana: profileInformation.lastNameKana,
            },
        ],
        thumbnailPicture: {},
    }), [email, profileInformation]);
    /**
     * フォームの初期値
     * @type {*}
     */
    const initialValues = useMemo(() => {
        var _a, _b;
        // 新規作成の場合
        if (!seminar.id) {
            return Object.assign(Object.assign({}, defaultValues), { status: SEMINAR_INFORMATION_STATUS_DRAFT.value });
        }
        // 取得した値を初期値として整形する
        const values = Object.assign({ id: seminar.id }, _.pick(seminar.information || {}, [
            'adjustSchedule',
            'applicationDeadline',
            'applicationProcess',
            'applicationUrl',
            'categories',
            'dates',
            'description',
            'documentFile',
            'handlePersonalInformation',
            'liaisons',
            'metaDescription',
            'participationFee',
            'participationTypes',
            'privacyPolicyUrl',
            'solveIssue',
            'status',
            'thumbnailPicture',
            'title',
            'type',
        ]));
        return Object.assign(Object.assign({}, values), { applicationDeadline: values.applicationDeadline
                ? moment(values.applicationDeadline).toDate()
                : undefined, 
            // 日程や連絡先が未設定の場合はデフォルト値を設定する
            dates: values.dates.length
                ? values.dates.map((v) => ({
                    endDate: v.endDate ? moment(v.endDate).toDate() : undefined,
                    // 文字列をDateにキャスト
                    startDate: v.startDate ? moment(v.startDate).toDate() : undefined,
                }))
                : defaultValues.dates, documentFile: values.documentFile || defaultValues.documentFile, handlePersonalInformation: values.handlePersonalInformation ||
                defaultValues.handlePersonalInformation, liaisons: values.liaisons.length
                ? values.liaisons
                : defaultValues.liaisons, thumbnailPicture: {
                id: values.thumbnailPicture.id,
                name: values.thumbnailPicture.name,
                path: ((_a = values.thumbnailPicture.webp) === null || _a === void 0 ? void 0 : _a.src) ||
                    ((_b = values.thumbnailPicture.default) === null || _b === void 0 ? void 0 : _b.src) ||
                    '',
            } });
    }, [seminar, defaultValues]);
    const { uploadFile, uploadingCount } = useResourceUpload({
        dispatch,
        unmountRef,
    });
    /**
     * ファイル変更時のイベント
     * @type {(function(*, *=, *): Promise<void>)|*}
     */
    const rawOnChangeFileInput = useCallback((file, fileType) => __awaiter(void 0, void 0, void 0, function* () {
        const result = yield uploadFile(file, fileType, ({ message }) => {
            notifyError(message);
        }, () => {
            notifyError();
        });
        if (!unmountRef.current) {
            if (result) {
                notify('アップロードが完了しました');
            }
        }
        return result;
    }), [uploadFile, unmountRef]);
    // ファイル選択時のイベント
    const [onChangeFileInput, isUploading] = usePromiseDoingCallback(rawOnChangeFileInput, unmountRef);
    /**
     * フォームの入力値で作成/更新apiを実行する
     * @type {function(*=): *}
     */
    const upsertFormValues = useCallback((formValues) => __awaiter(void 0, void 0, void 0, function* () {
        const rawParams = cloneDeep(formValues);
        const { dataType: rawDataType, dates: rawDates, id: initialId, liaisons: rawLiaisons, } = rawParams;
        // 入力値を整形
        const dates = rawDates.filter((v) => v.startDate || v.endDate);
        const liaisons = rawLiaisons.filter((v) => v.email || v.firstName || v.lastName);
        const params = Object.assign(Object.assign({}, rawParams), { dates, isPreview: rawDataType === SEMINAR_INFORMATION_DATA_TYPE_PREVIEW.value ? 1 : 0, liaisons });
        // IDの有無に応じてapi実行先を切り替える
        const action = !initialId
            ? apiCreateProfessionalSeminar(params)
            : apiUpdateProfessionalSeminar(initialId, params);
        // api実行
        const { payload } = yield wrapDispatch(dispatch, action);
        return payload;
    }), [dispatch]);
    /**
     * フォームをsubmit
     * @type {(function(*): Promise<void>)|*}
     */
    const onSubmit = useCallback((formValues) => __awaiter(void 0, void 0, void 0, function* () {
        const { dataType: rawDataType, status } = formValues;
        // 公開状態に応じた保存時のメッセージ
        const savedMessage = parseInt(status, 10) === SEMINAR_INFORMATION_STATUS_DRAFT.value
            ? '下書きに保存しました'
            : '公開しました';
        // 入力値の変更がない場合、以降の更新処理を実行しない
        if (pristine) {
            if (id && rawDataType === SEMINAR_INFORMATION_DATA_TYPE_PREVIEW.value) {
                // 既にデータが存在し、かつプレビュー保存の場合はプレビュー画面を表示させる
                window.open(`/seminar?${stringify({ preview: 'on', previewId: id })}`, '_blank');
            }
            else if (rawDataType === SEMINAR_INFORMATION_DATA_TYPE_VIEW.value) {
                // 通常保存の場合、メッセージを表示するだけ
                notify(savedMessage);
            }
            return;
        }
        // 更新処理を実行
        const { id: newId } = yield upsertFormValues(formValues);
        if (unmountRef.current) {
            return;
        }
        // データを引き直す
        yield wrapDispatch(dispatch, apiGetOneProfessionalSeminar(newId, { id: newId }));
        if (unmountRef.current) {
            return;
        }
        // submit実行元に応じて処理を分岐させる
        switch (rawDataType) {
            // 「保存する」が実行された
            case SEMINAR_INFORMATION_DATA_TYPE_VIEW.value: {
                notify(savedMessage);
                history.replace(`/seminar/${newId}/edit`);
                break;
            }
            // 「プレビュー」が実行された
            case SEMINAR_INFORMATION_DATA_TYPE_PREVIEW.value: {
                // プレビュー用にセミナー一覧画面のタブを開く
                window.open(`/seminar?${stringify({ preview: 'on', previewId: newId })}`, '_blank');
                break;
            }
            default: {
                break;
            }
        }
    }), [history, dispatch, id, pristine, unmountRef, upsertFormValues]);
    /**
     * セミナーの複製を実行
     * @type {(function(*): Promise<void>)|*}
     */
    const rawOnClone = useCallback((e) => __awaiter(void 0, void 0, void 0, function* () {
        // セミナーのIDを取得
        const { value } = e.currentTarget;
        yield wrapNotifyError(() => __awaiter(void 0, void 0, void 0, function* () {
            // セミナーの複製を実行
            const { payload: { id: newId }, } = yield wrapDispatch(dispatch, apiCreateProfessionalSeminarClone(value));
            if (unmountRef.current) {
                return;
            }
            // 複製したデータを引いておく
            yield wrapDispatch(dispatch, apiGetOneProfessionalSeminar(newId, { id: newId }));
            if (unmountRef.current) {
                return;
            }
            history.push(`/seminar/${newId}/edit`);
            notify('セミナーを複製しました');
        }));
    }), [history, dispatch, unmountRef]);
    const [onClone, isCloning] = usePromiseDoingCallback(rawOnClone, unmountRef);
    /**
     * バリデーションエラーが表示されているフィールドまでスクロールする
     * @type {(function(): void)|*}
     */
    const scrollToFirstErrorField = useCallback(() => {
        const { errors } = formRef.current.getState();
        const dots = Object.keys(toDotArray(errors || {}, '', {}, true));
        if (dots.length) {
            formValidateScroll(dots[0], { offset: -(window.innerHeight * 0.25) });
        }
    }, [formRef]);
    /**
     * 「保存する」「プレビューを見る」のクリックイベント
     * @type {(function(*): Promise<void>)|*}
     */
    const onClickSave = useCallback((e) => {
        const { value } = e.currentTarget;
        formRef.current.change('dataType', parseInt(value, 10));
        formRef.current.submit();
        scrollToFirstErrorField();
    }, [formRef, scrollToFirstErrorField]);
    /**
     * フォームを描画
     * @type {*}
     */
    const renderForm = useCallback((renderProps) => {
        formRef.current = renderProps.form;
        return (_jsxs(_Fragment, { children: [_jsx(SeminarOneForm, Object.assign({}, renderProps, { categories: categories, datepickerProps: datepickerProps, defaultValues: defaultValues, disabled: !isSelf, onChangeFileInput: onChangeFileInput, uploadingCount: uploadingCount })), _jsx(FinalFormSpy, { onChange: changeFormSpyState })] }));
    }, [
        categories,
        datepickerProps,
        defaultValues,
        formRef,
        isSelf,
        onChangeFileInput,
        uploadingCount,
    ]);
    // 画面描画時の初期実行
    useDidMount(() => {
        // カテゴリーの取得
        dispatch(apiGetCategorySelect('seminar_category'));
    });
    return {
        formSpyState,
        initialValues,
        isCloning,
        isSelf,
        isUploading,
        onChangeFileInput,
        onClickSave,
        onClone,
        onSubmit,
        renderForm,
    };
};
export default useProfessionalSeminarEdit;
